/* eslint-disable react/style-prop-object */
/* eslint-disable no-nested-ternary */
import { useQuery } from "@apollo/client";
import type { FilterableIndicator } from "components/screener/v2/ScreenerFilterableIndicators";
import { FilterRuleDefinitionHeader } from "components/screener/v2/filters/FilterRuleDefinition";
import type { ComplexComparisonFilterRuleInput, FilterCondition } from "components/screener/v2/filters/types";
import FilterableIndicatorValuesQuery from "components/screener/v2/query/FilterableIndicatorValuesQuery";
import type {
  IFilterableIndicatorValuesQuery,
  IFilterableIndicatorValuesQueryVariables,
} from "components/screener/v2/query/__generated__/FilterableIndicatorValuesQuery.generated";
import { ResponsiveComboBox } from "components/ui/Combobox";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import { Separator } from "components/ui/Separator";
import classNames from "components/ui/classNames";
import type { ReactNode } from "react";
import { startTransition, useState } from "react";
import type { IScreenerContextInput } from "types/graphqlTypes";

interface RichUIFilterRuleDefinitionProps {
  hideCounts?: boolean;
  screenerContext: IScreenerContextInput;
  condition?: Extract<FilterCondition, { type: "COMPARISON" }> | null;
  indicator: Extract<FilterableIndicator, { __typename: "ScreenerStaticIndicator" }>;
  children: ReactNode;
  onRemoveFilter: () => void;
  onChange: (input: ComplexComparisonFilterRuleInput | null) => void;
}
export function RichUIFilterRuleDefinition({
  hideCounts = false,
  screenerContext: context,
  condition,
  indicator,
  children,
  onRemoveFilter,
  onChange,
}: RichUIFilterRuleDefinitionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data, previousData, loading } = useQuery<
    IFilterableIndicatorValuesQuery,
    IFilterableIndicatorValuesQueryVariables
  >(FilterableIndicatorValuesQuery, {
    variables: {
      objectId: indicator.objectId,
      context,
    },
  });

  const filterableIndicatorValues = data?.filterableIndicatorValues || previousData?.filterableIndicatorValues;
  // Don't know what to do with this yet
  if (filterableIndicatorValues?.__typename !== "ScreenerFilterableIndicatorOptionsList") {
    return (
      <div aria-hidden={true} className="pointer-events-none animate-pulse opacity-50">
        {children}
      </div>
    );
  }
  const options = filterableIndicatorValues?.options ?? [];

  return (
    <Popover modal={true} open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
      <PopoverTrigger asChild={true}>{children}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className={classNames(
          "relative flex w-80 max-w-xs flex-col gap-y-2 rounded-2xl border border-zinc-600/40 py-2 shadow-lg shadow-zinc-300",
          "dark:border-slate-700 dark:shadow-2xl dark:shadow-slate-900",
        )}
        sideOffset={8}
      >
        <FilterRuleDefinitionHeader
          indicator={indicator}
          appliedCondition={condition}
          onOpenChange={setIsOpen}
          onRemoveFilter={() => {
            startTransition(() => {
              onRemoveFilter();
              setIsOpen(false);
            });
          }}
        />
        <Separator />

        {/* Loading Blankslate */}
        {loading && (
          <div className="flex px-2 py-1" aria-hidden={true}>
            <div
              className={classNames(
                "h-20 w-full animate-pulse rounded-md bg-slate-200",
                "dark:bg-slate-700 dark:ring-slate-700 dark:ring-offset-slate-700",
                "flex items-center justify-center",
              )}
            />
          </div>
        )}

        <div className="px-1">
          <ResponsiveComboBox
            isOpen={isOpen}
            value={condition?.value ?? null}
            hideCounts={hideCounts}
            onOpenChange={setIsOpen}
            onChange={(value) => {
              if (value === null) {
                onChange(null);
                return;
              }

              onChange({
                type: "COMPLEX",
                condition: {
                  type: "COMPARISON",
                  operator: "EQ",
                  value,
                },
              });
            }}
            options={options.map((option) => ({
              value: option.value,
              label: option.label,
              count: option.count ?? 0,
            }))}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
